import { useQuery } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import { useAuth } from "app/providers/AuthProvider";
import metadataQueryKeys from "shared/services/metadataQueryKeys";

const TRANSLATION_MANAGE_ROLES_PROPERTY = "prm.translations.manage_roles";
const PLATFORM_ADMINISTRATOR_ROLE = "platform_administrator";

// Mirrors the property's seeded value — used only while the property query hasn't
// resolved yet, or if the property record is missing in an environment that hasn't
// run the v4.11 seed. This is a UX gate, not a security boundary: the real
// enforcement is the `sys_translations_management` ACL required to actually save a
// translation (see .codex/02-contracts/translations.md).
const DEFAULT_TRANSLATION_MANAGE_ROLES = [
    "translator",
    "metadata_developer",
    "automation_developer",
    "integration_developer",
    PLATFORM_ADMINISTRATOR_ROLE,
];

const parseRoles = (raw: unknown): string[] => {
    if (Array.isArray(raw)) {
        return raw.map(String).map((role) => role.trim()).filter(Boolean);
    }

    if (typeof raw !== "string") {
        return [];
    }

    return raw.split(",").map((role) => role.trim()).filter(Boolean);
};

/**
 * Gates visibility of the translate icon on `TranslatableInput` — only roles
 * associated with platform configuration/development/translation should see it.
 * Configurable via the `sys_properties` record `prm.translations.manage_roles`
 * (comma-separated role names).
 */
export const useCanManageTranslations = (): boolean => {
    const { user } = useAuth();
    const userRoles = new Set((user?.roles ?? []).map(String));
    const isPlatformAdministrator = userRoles.has(PLATFORM_ADMINISTRATOR_ROLE);
    const { data } = useQuery({
        queryKey: metadataQueryKeys.property(TRANSLATION_MANAGE_ROLES_PROPERTY),
        queryFn: () =>
            new RecordManagement<{ value?: string }>("sys_properties")
                .where("name", TRANSLATION_MANAGE_ROLES_PROPERTY)
                .first(),
        staleTime: 1000 * 60 * 5,
        retry: false,
        enabled: Boolean(user) && !isPlatformAdministrator,
    });

    if (isPlatformAdministrator) {
        return true;
    }

    const configuredRoles = data?.value ? parseRoles(data.value) : DEFAULT_TRANSLATION_MANAGE_ROLES;
    return configuredRoles.some((role) => userRoles.has(role));
};
